import React from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, XCircle, RefreshCw, Lock, Unlock } from "lucide-react";
import { IMPORTANCE_LABELS } from "../OrderImportanceBadge";

const normalizeLookupValue = (value) => (value || "").trim().toLowerCase();

// Product Groups for customer spending analysis
const PRODUCT_GROUPS = [
  "GROCERIES",
  "CLEANING MATERIALS", 
  "HARDWARE",
  "GARDEN SUPPLIES",
  "IRRIGATION",
  "AUTOSHOP",
  "ANIMAL CARE",
  "CONSUMER PRODUCTS",
  "RATIONS",
  "BUILDING MATERIAL"
];

export const GeneralTab = ({
  formData,
  handleChange,
  categories,
  departments,
  suppliersList,
  isNew,
  autoGenerateBarcode,
  setAutoGenerateBarcode,
  handleCategoryChange,
  selectedCategoryId,
  filteredDepartments,
  showAddCategory,
  setShowAddCategory,
  showAddDepartment,
  setShowAddDepartment,
  newCategory,
  setNewCategory,
  newDepartment,
  setNewDepartment,
  handleAddCategory,
  handleAddDepartment,
  onGeneratePlu,
  generatingPlu,
}) => {
  // Field lock system — locked fields persist their values when creating new products
  const LOCK_STORAGE_KEY = "product_field_locks";
  const getLocks = () => {
    try { return JSON.parse(localStorage.getItem(LOCK_STORAGE_KEY) || "{}"); } catch { return {}; }
  };
  const isLocked = (field) => !!getLocks()[field];
  const toggleLock = (field) => {
    const locks = getLocks();
    if (locks[field]) {
      delete locks[field];
    } else {
      locks[field] = formData[field] || "";
    }
    localStorage.setItem(LOCK_STORAGE_KEY, JSON.stringify(locks));
    // Force re-render
    setLockVersion(v => v + 1);
  };
  const [lockVersion, setLockVersion] = React.useState(0);

  // When a locked field value changes, update the stored lock value
  const wrappedHandleChange = (field, value) => {
    handleChange(field, value);
    const locks = getLocks();
    if (locks[field] !== undefined) {
      locks[field] = value || "";
      localStorage.setItem(LOCK_STORAGE_KEY, JSON.stringify(locks));
    }
  };

  const LockButton = ({ field }) => {
    const locked = isLocked(field);
    return (
      <button
        type="button"
        onClick={() => toggleLock(field)}
        className={`ml-1 p-0.5 rounded transition-colors ${locked ? 'text-amber-600 hover:text-amber-800' : 'text-gray-300 hover:text-gray-500'}`}
        title={locked ? `Unlock ${field} (currently locked to "${getLocks()[field]}")` : `Lock ${field} for next new product`}
        data-testid={`lock-${field}`}
      >
        {locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5" />}
      </button>
    );
  };

  const sortedCategoryOptions = [...categories].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const hasCurrentCategoryInOptions = sortedCategoryOptions.some(
    (cat) => normalizeLookupValue(cat.name) === normalizeLookupValue(formData.category)
  );
  const categoryOptions = formData.category && !hasCurrentCategoryInOptions
    ? [{ id: `legacy-category-${formData.category}`, name: formData.category, legacy: true }, ...sortedCategoryOptions]
    : sortedCategoryOptions;

  const sortedDepartmentOptions = [...filteredDepartments].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
  const hasCurrentDepartmentInOptions = sortedDepartmentOptions.some(
    (dept) => normalizeLookupValue(dept.name) === normalizeLookupValue(formData.department)
  );
  const departmentOptions = formData.department && !hasCurrentDepartmentInOptions
    ? [{ id: `legacy-department-${formData.department}`, name: formData.department, legacy: true }, ...sortedDepartmentOptions]
    : sortedDepartmentOptions;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>PLU Code {isNew && <span className="text-red-500">*</span>}</Label>
          <div className="flex gap-2">
            <Input
              value={formData.plu_code || ""}
              onChange={(e) => {
                // Strip leading zeros from PLU code
                let value = e.target.value;
                if (value && /^\d+$/.test(value)) {
                  value = value.replace(/^0+/, '') || '0';
                }
                handleChange("plu_code", value);
              }}
              placeholder={isNew ? "Auto-generated or enter manually" : ""}
              className="flex-1"
            />
            {isNew && (
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={onGeneratePlu}
                disabled={generatingPlu}
                title="Generate PLU Code"
              >
                <RefreshCw className={`h-4 w-4 ${generatingPlu ? 'animate-spin' : ''}`} />
              </Button>
            )}
          </div>
          <Label className="mt-3 block">Order Importance</Label>
          <Select
            value={formData.order_importance ? String(formData.order_importance) : "none"}
            onValueChange={(v) => {
              handleChange("order_importance", v === "none" ? null : Number(v));
              handleChange("order_importance_manual", v !== "none");
            }}
          >
            <SelectTrigger data-testid="product-order-importance-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Not set</SelectItem>
              {[1, 2, 3].map((n) => (
                <SelectItem key={n} value={String(n)} data-testid={`order-importance-option-${n}`}>
                  {n} — {IMPORTANCE_LABELS[n]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-gray-500 mt-1" data-testid="product-order-importance-source">
            {formData.order_importance
              ? (formData.order_importance_manual ? "Set by hand — the auto button won't change it" : "Set automatically from sales")
              : "1 = must always be ordered · 3 = not so important"}
          </p>
        </div>
        <div>
          <Label>Barcodes <span className="text-gray-400 text-xs">(up to 5)</span></Label>
          <div className="space-y-2">
            {/* Primary barcode input - always shown */}
            <div className="flex gap-2 items-center">
              <Input
                value={(formData.barcodes || [])[0] || ""}
                onChange={(e) => {
                  const newBarcodes = [...(formData.barcodes || [])];
                  if (newBarcodes.length === 0) {
                    newBarcodes.push(e.target.value);
                  } else {
                    newBarcodes[0] = e.target.value;
                  }
                  handleChange("barcodes", newBarcodes);
                  handleChange("barcode", e.target.value);
                  if (e.target.value) setAutoGenerateBarcode(false);
                }}
                placeholder={autoGenerateBarcode ? "Will be auto-generated" : "Enter barcode"}
                disabled={autoGenerateBarcode}
                className={`flex-1 ${autoGenerateBarcode ? "bg-gray-100" : "border-green-300"}`}
              />
              <span className="text-xs text-green-600 whitespace-nowrap">Primary</span>
              <Button
                type="button"
                variant={autoGenerateBarcode ? "default" : "outline"}
                size="sm"
                onClick={() => {
                  setAutoGenerateBarcode(!autoGenerateBarcode);
                  if (!autoGenerateBarcode) {
                    handleChange("barcodes", []);
                    handleChange("barcode", "");
                  }
                }}
                className={`whitespace-nowrap ${autoGenerateBarcode ? "bg-green-600 hover:bg-green-700" : ""}`}
              >
                {autoGenerateBarcode ? "✓ AUTO" : "AUTO"}
              </Button>
            </div>
            
            {/* Additional barcodes */}
            {(formData.barcodes || []).slice(1).map((bc, idx) => (
              <div key={idx + 1} className="flex gap-2 items-center">
                <Input
                  value={bc}
                  onChange={(e) => {
                    const newBarcodes = [...(formData.barcodes || [])];
                    newBarcodes[idx + 1] = e.target.value;
                    handleChange("barcodes", newBarcodes);
                  }}
                  placeholder={`Barcode ${idx + 2}`}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    const newBarcodes = (formData.barcodes || []).filter((_, i) => i !== idx + 1);
                    handleChange("barcodes", newBarcodes);
                  }}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                >
                  <XCircle className="h-4 w-4" />
                </Button>
              </div>
            ))}
            
            {/* Add barcode button */}
            {(formData.barcodes || []).length > 0 && (formData.barcodes || []).length < 5 && !autoGenerateBarcode && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  const newBarcodes = [...(formData.barcodes || []), ""];
                  handleChange("barcodes", newBarcodes);
                }}
                className="w-full border-dashed"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Barcode ({(formData.barcodes || []).length}/5)
              </Button>
            )}
            
            {autoGenerateBarcode && (
              <p className="text-xs text-green-600">Primary barcode will be auto-generated on save</p>
            )}
          </div>
        </div>
      </div>

      <div>
        <Label>Description {isNew && <span className="text-red-500">*</span>}</Label>
        <Input
          value={formData.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder={isNew ? "Enter product name/description" : ""}
        />
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="flex items-center">Category <LockButton field="category" /></Label>
          <Select value={formData.category} onValueChange={(value) => {
            handleCategoryChange(value);
            const locks = getLocks();
            if (locks.category !== undefined) {
              locks.category = value || "";
              localStorage.setItem(LOCK_STORAGE_KEY, JSON.stringify(locks));
            }
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...categoryOptions].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(cat => (
                <SelectItem key={cat.id} value={cat.name}>
                  {cat.name}
                  {cat.legacy && <span className="text-xs text-amber-500 ml-1">(legacy)</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!showAddCategory && (
            <Button
              type="button"
              variant="link"
              className="text-xs p-0 h-auto mt-1"
              onClick={() => setShowAddCategory(true)}
            >
              + Add New Category
            </Button>
          )}
          {showAddCategory && (
            <div className="flex gap-2 mt-2">
              <Input
                value={newCategory}
                onChange={(e) => setNewCategory(e.target.value)}
                placeholder="New category name"
                className="text-sm"
              />
              <Button type="button" size="sm" onClick={handleAddCategory}>Add</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowAddCategory(false)}>Cancel</Button>
            </div>
          )}
        </div>
        <div>
          <Label className="flex items-center">Department <LockButton field="department" /> {selectedCategoryId && <span className="text-xs text-gray-500 ml-1">(in {formData.category})</span>}</Label>
          <Select value={formData.department || "none"} onValueChange={(value) => wrappedHandleChange("department", value === "none" ? null : value)}>
            <SelectTrigger>
              <SelectValue placeholder="Select department (optional)" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">None</SelectItem>
              {[...departmentOptions].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map(dept => (
                <SelectItem key={dept.id} value={dept.name}>
                  {dept.name}
                  {dept.legacy && <span className="text-xs text-amber-500 ml-1">(legacy)</span>}
                  {!dept.legacy && !dept.category_id && <span className="text-xs text-gray-400 ml-1">(unassigned)</span>}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {!showAddDepartment && selectedCategoryId && (
            <Button
              type="button"
              variant="link"
              className="text-xs p-0 h-auto mt-1"
              onClick={() => setShowAddDepartment(true)}
            >
              + Add New Department to {formData.category}
            </Button>
          )}
          {!showAddDepartment && !selectedCategoryId && (
            <p className="text-xs text-gray-500 mt-1">Select a category first to add departments</p>
          )}
          {showAddDepartment && (
            <div className="flex gap-2 mt-2">
              <Input
                value={newDepartment}
                onChange={(e) => setNewDepartment(e.target.value)}
                placeholder={`New department in ${formData.category}`}
                className="text-sm"
              />
              <Button type="button" size="sm" onClick={handleAddDepartment}>Add</Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setShowAddDepartment(false)}>Cancel</Button>
            </div>
          )}
        </div>
        
        {/* Product Group - for customer spending analysis */}
        <div>
          <Label className="flex items-center">Product Group <LockButton field="product_group" /> <span className="text-xs text-gray-400 ml-1">(for customer spending analysis)</span></Label>
          <Select 
            value={formData.product_group || "none"} 
            onValueChange={(value) => wrappedHandleChange("product_group", value === "none" ? null : value)}
          >
            <SelectTrigger data-testid="product-group-select">
              <SelectValue placeholder="Select product group" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">Unassigned</SelectItem>
              {[...PRODUCT_GROUPS].sort().map(group => (
                <SelectItem key={group} value={group}>{group}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-gray-500 mt-1">Used to categorize spending on customer statements</p>
        </div>
      </div>

      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Unit</Label>
          <Input
            value={formData.unit}
            onChange={(e) => handleChange("unit", e.target.value)}
          />
        </div>
        <div>
          <Label>Case Size <span className="text-xs text-gray-400">(units per case)</span></Label>
          <Input
            type="number"
            min="1"
            value={formData.case_size || ""}
            onChange={(e) => handleChange("case_size", e.target.value ? parseInt(e.target.value) : null)}
            placeholder="e.g., 6 for 6-pack"
          />
          {formData.case_size && formData.case_size > 1 && (
            <p className="text-xs text-blue-600 mt-1">Orders should be in multiples of {formData.case_size}</p>
          )}
        </div>
        <div>
          <Label>Weight per Unit (kg) <span className="text-xs text-gray-400">(for weight suppliers)</span></Label>
          <Input
            type="number"
            min="0"
            step="0.001"
            value={formData.weight_kg ?? ""}
            onChange={(e) => handleChange("weight_kg", e.target.value === "" ? 0 : parseFloat(e.target.value))}
            placeholder="e.g., 25 (kg)"
            data-testid="product-weight-kg-input"
          />
          <p className="text-xs text-gray-500 mt-1">
            Used to enforce supplier minimum weight orders (e.g. 1000 kg per order).
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label className="flex items-center">Supplier <LockButton field="supplier" /></Label>
          <Select
            value={formData.supplier || "none"}
            onValueChange={(value) => {
              const actualValue = value === "none" ? "" : value;
              wrappedHandleChange("supplier", actualValue);
              const matchingSupplier = suppliersList.find(s => s.name === actualValue);
              if (matchingSupplier) {
                handleChange("supplier_id", matchingSupplier.id);
              } else {
                handleChange("supplier_id", "");
              }
            }}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select a supplier" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">-- None --</SelectItem>
              {[...suppliersList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((supplier) => (
                <SelectItem key={supplier.id} value={supplier.name}>
                  {supplier.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Supplier PLU</Label>
          <Input
            value={formData.supplier_plu || ""}
            onChange={(e) => handleChange("supplier_plu", e.target.value)}
          />
        </div>
      </div>

      {/* Alternative Supplier */}
      <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
        <Label className="text-amber-800 font-semibold mb-2 block">Alternative Supplier (Backup)</Label>
        <p className="text-xs text-amber-600 mb-3">If preferred supplier is out of stock, use this supplier</p>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-sm">Alternative Supplier</Label>
            <Select
              value={formData.alternative_supplier || "none"}
              onValueChange={(value) => {
                const actualValue = value === "none" ? "" : value;
                handleChange("alternative_supplier", actualValue);
                const matchingSupplier = suppliersList.find(s => s.name === actualValue);
                if (matchingSupplier) {
                  handleChange("alternative_supplier_id", matchingSupplier.id);
                } else {
                  handleChange("alternative_supplier_id", "");
                }
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select alternative supplier" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">-- None --</SelectItem>
                {[...suppliersList].sort((a, b) => (a.name || '').localeCompare(b.name || '')).map((supplier) => (
                  <SelectItem key={supplier.id} value={supplier.name}>
                    {supplier.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-sm">Alternative Supplier PLU</Label>
            <Input
              value={formData.alternative_supplier_plu || ""}
              onChange={(e) => handleChange("alternative_supplier_plu", e.target.value)}
              placeholder="Supplier's product code"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
